import React, { useState } from 'react';
import { Box, Typography, Paper, Button, InputBase, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Send as SendIcon, Save as SaveIcon } from '@mui/icons-material';
import VoiceWaveform from '../pages/Landing/components/VoiceWaveform.tsx';
import { getAvatarComponent } from '../utils/getAvatarComponent.tsx';

const ProposalContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const VoiceContainer = styled(motion(Paper))(({ theme }) => ({
  padding: theme.spacing(4),
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  maxWidth: 600,
  width: '100%',
  marginTop: theme.spacing(4),
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(45deg, #2196f3 30%, #8a2be2 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const ProposalInput = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '600px',
  margin: '20px auto',
  background: 'rgba(57, 63, 84, 0.8)',
  padding: '1.4rem 2rem',
  borderRadius: theme.spacing(1),
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '2px',
    background: 'linear-gradient(to right, #B294FF, #57E6E6, #FEFFB8, #57E6E6, #B294FF, #57E6E6)',
    backgroundSize: '500% auto',
    animation: 'gradient 3s linear infinite',
  }
}));

const RecordingButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isRecording',
})<{ isRecording?: boolean }>(({ theme, isRecording }) => ({
  background: isRecording 
    ? 'linear-gradient(45deg, #f44336 30%, #ff9800 90%)'
    : 'linear-gradient(45deg, #2196f3 30%, #8a2be2 90%)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: '160px',
  padding: theme.spacing(1, 3),
  transition: 'all 0.3s ease',
  '&:hover': {
    background: isRecording
      ? 'linear-gradient(45deg, #d32f2f 30%, #f57c00 90%)'
      : 'linear-gradient(45deg, #1976d2 30%, #7b1fa2 90%)',
  }
}));

const WaveformBox = styled(Box)<{ $isRecording: boolean }>(({ $isRecording }) => ({
  transform: 'scale(1.2)',
  opacity: $isRecording ? 1 : 0.5,
  transition: 'all 0.3s ease',
  width: '120px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.9,
  }
}));

const ActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  justifyContent: 'space-between',
  marginTop: theme.spacing(2),
}));

const ProposalSubmission: React.FC = () => {
  const [proposalText, setProposalText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);

  const handleRecordingClick = () => {
    setIsRecording(!isRecording);
    if (isRecording) {
      // Simulate saving a recording
      setHasRecording(true);
    }
  };

  const handleSubmit = () => {
    // Handle submission logic here
    console.log('Submitting proposal:', proposalText);
    // Reset form
    setProposalText('');
    setHasRecording(false);
  };

  const handleSaveRecording = () => {
    // Handle saving recording logic here
    console.log('Saving recording');
    setHasRecording(true);
    setIsRecording(false);
  };

  // Get the VoiceIcon component
  const VoiceIcon = getAvatarComponent('VoiceIcon', isRecording ? '#00CC99' : '#7b1fa2', 24);

  return (
    <ProposalContainer>
      <GradientText variant="h4" gutterBottom>
        Submit Your Proposal
      </GradientText>

      <Typography variant="body1" sx={{ color: '#7881A1', mb: 4 }}>
        Use voice commands or type to create your proposal
      </Typography>

      <VoiceContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <ProposalInput>
          <InputBase
            multiline
            rows={4}
            fullWidth
            placeholder="Start speaking or type your proposal here..."
            value={proposalText}
            onChange={(e) => setProposalText(e.target.value)}
            sx={{
              color: '#BFD2FF',
              fontSize: '1.1rem',
              '&::placeholder': {
                color: '#7881A1',
              }
            }}
          />
        </ProposalInput>

        <ActionContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <RecordingButton
              variant="contained"
              size="large"
              isRecording={isRecording}
              onClick={handleRecordingClick}
              startIcon={VoiceIcon}
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </RecordingButton>
            <WaveformBox 
              $isRecording={isRecording}
              onClick={handleRecordingClick}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <VoiceWaveform isActive={isRecording} />
            </WaveformBox>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {(isRecording || hasRecording) && (
              <Tooltip title="Save Recording">
                <IconButton
                  onClick={handleSaveRecording}
                  sx={{
                    color: '#8a2be2',
                    '&:hover': { color: '#9c27b0' }
                  }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Submit Proposal">
              <IconButton
                onClick={handleSubmit}
                disabled={!proposalText && !hasRecording}
                sx={{
                  color: '#2196f3',
                  '&:hover': { color: '#1976d2' },
                  '&.Mui-disabled': {
                    color: 'rgba(33, 150, 243, 0.3)',
                  }
                }}
              >
                <SendIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </ActionContainer>
      </VoiceContainer>
    </ProposalContainer>
  );
};

export default ProposalSubmission; 